import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Search, Loader2, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API } from "@/lib/api";

const steps = [
  { key: "new", label: "Enquiry received" },
  { key: "contacted", label: "Contacted by advisor" },
  { key: "in-progress", label: "Application in progress" },
  { key: "closed", label: "Process completed" },
];

export default function Track() {
  const [ref, setRef] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const res = await axios.get(`${API}/track`, { params: { ref: ref.trim(), phone: phone.trim() } });
      setResult(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not find your application");
    } finally {
      setLoading(false);
    }
  };

  const activeIndex = result ? steps.findIndex((s) => s.key === result.status) : -1;

  return (
    <div data-testid="track-page" className="min-h-screen bg-night text-foreground">
      <header className="border-b border-white/10 bg-night/80 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="track-brand" className="flex items-center">
            <span className="bg-white rounded-md px-3 py-1.5 flex items-center shadow-md shadow-black/30">
              <img src="/globus-lockup.png" alt="Globus Financial Services" className="h-7 w-auto object-contain" />
            </span>
          </Link>
          <Link data-testid="track-back-link" to="/" className="text-sm text-[#A0AAB2] hover:text-gold transition-colors flex items-center gap-2">
            <ArrowLeft size={14} /> Back to site
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-20">
        <p className="overline-label mb-4">Application Tracker</p>
        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-4">Where is my application?</h1>
        <p className="text-[#A0AAB2] text-base md:text-lg leading-relaxed mb-12">
          Enter the reference ID you received after submitting your enquiry, along with your registered phone number.
        </p>

        <form onSubmit={submit} data-testid="track-form" className="bg-slate2 border border-white/10 p-8 space-y-6">
          <div>
            <Label htmlFor="track-ref" className="text-sm text-[#A0AAB2] mb-2 block">Reference ID</Label>
            <Input
              data-testid="track-ref-input"
              id="track-ref"
              required
              value={ref}
              onChange={(e) => setRef(e.target.value)}
              placeholder="e.g. AB09E89B"
              className="bg-slate3 border-white/10 rounded-sm uppercase"
            />
          </div>
          <div>
            <Label htmlFor="track-phone" className="text-sm text-[#A0AAB2] mb-2 block">Registered phone number</Label>
            <Input
              data-testid="track-phone-input"
              id="track-phone"
              required
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+91 98XXX XXXXX"
              className="bg-slate3 border-white/10 rounded-sm"
            />
          </div>
          <button
            data-testid="track-submit-button"
            type="submit"
            disabled={loading}
            className="bg-gold text-night hover:bg-goldhover transition-colors rounded-sm px-7 py-3 font-semibold flex items-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
            {loading ? "Checking…" : "Track application"}
          </button>
          {error && <p data-testid="track-error" className="text-sm text-red-400">{error}</p>}
        </form>

        {result && (
          <div data-testid="track-result" className="mt-8 bg-slate2 border border-white/10 p-8">
            <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground">Application status</p>
            <h2 className="font-serif text-2xl mt-2">{result.name}</h2>
            <p className="text-sm text-[#A0AAB2] mt-1">
              {result.loan_type} · Enquired on{" "}
              {new Date(result.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
            </p>
            <div className="mt-8 space-y-0">
              {steps.map((s, i) => {
                const done = i <= activeIndex;
                return (
                  <div key={s.key} data-testid={`track-step-${s.key}`} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <span className={`h-7 w-7 rounded-full border flex items-center justify-center shrink-0 ${done ? "bg-gold border-gold text-night" : "border-white/20 text-muted-foreground"}`}>
                        {done ? <Check size={14} /> : <span className="text-[10px]">{i + 1}</span>}
                      </span>
                      {i < steps.length - 1 && <span className={`w-px flex-1 min-h-[28px] ${i < activeIndex ? "bg-gold" : "bg-white/10"}`} />}
                    </div>
                    <p className={`text-sm pt-1 pb-6 ${done ? "text-foreground font-medium" : "text-muted-foreground"}`}>{s.label}</p>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Questions? WhatsApp us at +91 87965 12777 with your reference ID.
            </p>
          </div>
        )}
      </main>

      <footer className="border-t border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-6 text-xs text-muted-foreground">
          © {new Date().getFullYear()} Globus Financial Services · Sahibabad, Uttar Pradesh 201005, India · GSTIN: 09AEFPK4400F1ZW
        </div>
      </footer>
    </div>
  );
}
